import React, { useMemo, memo } from 'react';
import { getTeamSeedsForMatch } from '../../utils/tournamentBracketUtils';
import { formatTournamentEntryLabel } from '../../utils/tournamentFormat';

const ROUND_ORDER = ['round_of_16', 'quarter_final', 'semi_final', 'bronze', 'final'];

const ROUND_META = {
  round_of_16: {
    short: 'R16',
    title: 'Round of 16',
    chip: 'bg-primary/12 text-primary border-primary/25 shadow-sm dark:bg-primary/20',
  },
  quarter_final: {
    short: 'QF',
    title: 'Quarter-finals',
    chip: 'bg-surface-elevated text-text-primary border-border-medium dark:bg-white/[0.06]',
  },
  semi_final: {
    short: 'SF',
    title: 'Semi-finals',
    chip: 'bg-accent/10 text-text-primary border-accent/25 dark:bg-accent/15',
  },
  bronze: {
    short: '3rd',
    title: 'Bronze / 3rd place',
    chip: 'bg-warning/12 text-warning-dark border-warning/35 dark:bg-warning/20',
  },
  final: {
    short: 'Final',
    title: 'Final',
    chip: 'bg-gradient-to-r from-warning/25 via-warning/15 to-warning/25 text-warning-dark border-warning/50 shadow-md dark:from-warning/20 dark:via-warning/10 dark:to-warning/20',
  },
};

const formatTeamShort = (team, maxLen = 22) => {
  if (!team?.player1) return 'TBD';
  const singles = !team.player2;
  const full = formatTournamentEntryLabel(team, singles);
  return full.length > maxLen ? `${full.slice(0, maxLen - 1)}…` : full;
};

/**
 * Single match tile — bracket tree
 */
const BracketMatchCell = memo(({ match, allMatches, onEnterScore, onEditScore, readOnly = false }) => {
  const [team1Seed, team2Seed] = getTeamSeedsForMatch(match, allMatches);
  const team1Name = match.team1 ? formatTeamShort(match.team1) : 'TBD';
  const team2Name = match.team2 ? formatTeamShort(match.team2) : 'TBD';
  const isMatchComplete = ['completed', 'walkover', 'forfeit'].includes(match.status);
  const score =
    isMatchComplete && match.games?.length > 0
      ? match.games.map((g) => `${g.team_1_score}-${g.team_2_score}`).join(', ')
      : null;
  const team1Won = isMatchComplete && match.winner_entry_id === match.entry1_id;
  const team2Won = isMatchComplete && match.winner_entry_id === match.entry2_id;
  const isFinal = match.round === 'final';
  const isBronze = match.round === 'bronze';
  const isLiveish = match.status === 'in_progress';

  const fullTeam1Title = match.team1 ? formatTournamentEntryLabel(match.team1, !match.team1.player2) : '';
  const fullTeam2Title = match.team2 ? formatTournamentEntryLabel(match.team2, !match.team2.player2) : '';

  const cellRing = isFinal
    ? 'ring-2 ring-warning/50 ring-offset-2 ring-offset-background dark:ring-offset-background'
    : isBronze
      ? 'ring-1 ring-warning/35'
      : '';

  return (
    <div
      className={`
        group/match relative w-full min-h-[104px] rounded-xl border overflow-hidden text-xs flex flex-col
        transition-all duration-200 ease-out
        ${isMatchComplete
          ? 'bg-gradient-to-b from-surface-elevated/90 to-surface border-border-medium shadow-md dark:from-white/[0.07] dark:to-surface dark:border-white/[0.08]'
          : readOnly
            ? 'bg-surface border-border-medium shadow-sm'
            : 'bg-gradient-to-b from-surface to-surface-subtle border-primary/35 shadow-md hover:border-primary hover:shadow-lg hover:-translate-y-0.5 cursor-pointer dark:from-surface dark:to-white/[0.03]'
        }
        ${cellRing}
        ${isLiveish && !isMatchComplete ? 'border-info/40 shadow-[0_0_12px_-2px_rgba(59,130,246,0.35)]' : ''}
      `}
      onClick={() => !readOnly && !isMatchComplete && onEnterScore?.(match)}
      role={!readOnly && !isMatchComplete ? 'button' : undefined}
      tabIndex={!readOnly && !isMatchComplete ? 0 : undefined}
      onKeyDown={(e) => {
        if (!readOnly && !isMatchComplete && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onEnterScore?.(match);
        }
      }}
    >
      {/* winner ribbons */}
      {(team1Won || team2Won) && isMatchComplete && (
        <div
          className={`absolute inset-x-0 top-0 h-1 ${isFinal ? 'bg-gradient-to-r from-warning/80 via-warning to-warning/80' : 'bg-gradient-to-r from-success/70 via-success to-success/70'}`}
          aria-hidden
        />
      )}

      <div className="flex flex-col flex-1 min-h-0 p-2 pt-2.5 gap-0">
        <div
          className={`flex justify-between items-center min-w-0 gap-1.5 py-1 px-1.5 rounded-md -mx-0.5 ${team1Won ? 'bg-success/10 font-semibold text-success-dark dark:text-success' : 'bg-transparent'}`}
        >
          <span className="truncate text-left" title={fullTeam1Title}>
            {team1Name}
            {team1Seed && <span className="text-text-muted ml-0.5 shrink-0 font-normal">({team1Seed})</span>}
          </span>
          {team1Won && (
            <span
              className={`shrink-0 text-xs ${isFinal ? 'text-warning-dark' : 'text-success'}`}
              title={isFinal ? 'Champions' : 'Won'}
              aria-hidden
            >
              {isFinal ? '👑' : '✓'}
            </span>
          )}
        </div>

        <div className="relative flex justify-center py-1">
          <div className="absolute inset-x-2 top-1/2 h-px bg-border-subtle dark:bg-white/10" aria-hidden />
          <span
            className={`relative z-[1] px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
              score
                ? 'bg-surface-elevated text-text-primary border border-border-medium dark:bg-white/10'
                : isLiveish
                  ? 'bg-info/15 text-info-dark border border-info/30'
                  : 'bg-surface-subtle text-text-muted border border-border-subtle dark:bg-white/[0.06]'
            }`}
          >
            {score || (isLiveish ? 'Live' : 'vs')}
          </span>
        </div>

        <div
          className={`flex justify-between items-center min-w-0 gap-1.5 py-1 px-1.5 rounded-md -mx-0.5 ${team2Won ? 'bg-success/10 font-semibold text-success-dark dark:text-success' : 'bg-transparent'}`}
        >
          <span className="truncate text-left" title={fullTeam2Title}>
            {team2Name}
            {team2Seed && <span className="text-text-muted ml-0.5 shrink-0 font-normal">({team2Seed})</span>}
          </span>
          {team2Won && (
            <span
              className={`shrink-0 text-xs ${isFinal ? 'text-warning-dark' : 'text-success'}`}
              title={isFinal ? 'Champions' : 'Won'}
              aria-hidden
            >
              {isFinal ? '👑' : '✓'}
            </span>
          )}
        </div>
      </div>

      {!readOnly && (
        <div className="flex gap-1 px-2 pb-2 pt-0 flex-shrink-0 justify-end border-t border-border-subtle/60 dark:border-white/[0.06] bg-surface-subtle/40 dark:bg-black/20">
          {isMatchComplete ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEditScore?.(match);
              }}
              className="px-2 py-1 rounded-md text-[10px] font-semibold bg-secondary hover:opacity-90 text-text-inverse border border-primary/30 transition-opacity"
              title="Edit score"
            >
              Edit
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEnterScore?.(match);
              }}
              className="px-2 py-1 rounded-md text-[10px] font-semibold bg-primary text-text-inverse border border-primary-dark transition-opacity hover:opacity-95"
              title="Enter score"
            >
              Score
            </button>
          )}
        </div>
      )}
    </div>
  );
});

/**
 * Bracket tree — knockout visualization
 */
const KnockoutBracketTree = ({ matches, allMatches, onEnterScore, onEditScore, readOnly = false }) => {
  const rounds = useMemo(() => {
    const byRound = {};
    ROUND_ORDER.forEach((r) => {
      byRound[r] = [];
    });
    (matches || []).forEach((m) => {
      if (byRound[m.round]) byRound[m.round].push(m);
    });
    return ROUND_ORDER.filter((r) => (byRound[r] || []).length > 0).map((r) => ({
      key: r,
      meta: ROUND_META[r] || {
        short: r.replace(/_/g, ' '),
        title: r,
        chip: 'bg-surface-elevated text-text-secondary border-border-medium',
      },
      matches: (byRound[r] || []).sort((a, b) => (a.match_order ?? 0) - (b.match_order ?? 0)),
    }));
  }, [matches]);

  const CARD_HEIGHT = 108;
  const CARD_GAP = 36;
  const HEADER_HEIGHT = 40;
  const firstRoundCount = rounds.length > 0 ? rounds[0].matches.length : 4;
  const matchesHeight = useMemo(() => {
    if (rounds.length === 0) return 300;
    const firstCount = rounds[0].matches.length;
    return firstCount * CARD_HEIGHT + (firstCount - 1) * CARD_GAP;
  }, [rounds]);

  const totalHeight = matchesHeight + HEADER_HEIGHT;

  const getMatchPosition = (roundIndex, matchIndex) => {
    const slotSize = Math.pow(2, roundIndex);
    return (matchIndex * slotSize + slotSize / 2) / firstRoundCount;
  };

  if (rounds.length === 0) return null;

  const BracketConnector = ({ prevCount, nextCount }) => {
    const w = 44;
    const h = matchesHeight;
    const midX = w / 2;

    const prevPositions = Array.from({ length: prevCount }, (_, i) => ((i + 0.5) / prevCount) * 100);
    const nextPositions = Array.from({ length: nextCount }, (_, i) => ((i * 2 + 1) / prevCount) * 100);

    const paths = [];
    prevPositions.forEach((y, i) => {
      paths.push(
        <line
          key={`h-${i}`}
          x1={0}
          y1={`${y}%`}
          x2={midX}
          y2={`${y}%`}
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          className="text-primary/35 dark:text-primary/45"
        />
      );
    });
    nextPositions.forEach((y, i) => {
      const prevY1 = prevPositions[i * 2];
      const prevY2 = prevPositions[i * 2 + 1];
      const midY = (prevY1 + prevY2) / 2;
      paths.push(
        <line
          key={`v-${i}`}
          x1={midX}
          y1={`${prevY1}%`}
          x2={midX}
          y2={`${prevY2}%`}
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          className="text-primary/35 dark:text-primary/45"
        />,
        <line
          key={`r-${i}`}
          x1={midX}
          y1={`${midY}%`}
          x2={w}
          y2={`${y}%`}
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          className="text-primary/35 dark:text-primary/45"
        />
      );
    });

    return (
      <div
        className="flex-shrink-0 flex flex-col"
        style={{ width: w + 20, height: totalHeight }}
        aria-hidden
      >
        <div style={{ height: HEADER_HEIGHT }} />
        <div className="flex items-center -mx-2" style={{ height: matchesHeight }}>
          <svg width={w} height={h} className="overflow-visible drop-shadow-[0_1px_6px_rgba(0,0,0,0.06)] dark:drop-shadow-[0_1px_8px_rgba(0,0,0,0.25)]">
            {paths}
          </svg>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-border-subtle bg-gradient-to-br from-surface via-surface-subtle/40 to-primary/[0.06] dark:from-surface dark:via-surface dark:to-primary/[0.12] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
      <div className="sm:hidden mb-0 p-3 border-b border-border-subtle/80 dark:border-white/[0.06] text-xs text-text-secondary bg-info/5 dark:bg-info/10">
        <span className="font-medium text-info-dark dark:text-info">Tip</span> — Swipe sideways to follow the bracket.
      </div>

      <div className="overflow-x-auto py-5 sm:py-7 px-3 sm:px-5">
        <div className="inline-flex items-stretch min-w-max gap-1 sm:gap-2">
          {rounds.map((round, roundIndex) => (
            <React.Fragment key={round.key}>
              {roundIndex > 0 && (
                <BracketConnector
                  prevCount={rounds[roundIndex - 1].matches.length}
                  nextCount={round.matches.length}
                />
              )}
              <div
                className="flex flex-col items-center flex-shrink-0 w-[172px] sm:w-[196px]"
                style={{ height: totalHeight }}
              >
                <div
                  className="flex-shrink-0 w-full flex justify-center mb-1"
                  style={{ height: HEADER_HEIGHT }}
                >
                  <span
                    title={round.meta.title}
                    className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${round.meta.chip}`}
                  >
                    {round.meta.short}
                  </span>
                </div>
                <div className="relative w-full flex-shrink-0 flex-1" style={{ height: matchesHeight }}>
                  {round.matches.map((match, matchIndex) => {
                    const topPercent = getMatchPosition(roundIndex, matchIndex) * 100;
                    return (
                      <div
                        key={match.id}
                        className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 flex justify-center w-[calc(100%-4px)]"
                        style={{ top: `${topPercent}%` }}
                      >
                        <BracketMatchCell
                          match={match}
                          allMatches={allMatches || matches}
                          onEnterScore={onEnterScore}
                          onEditScore={onEditScore}
                          readOnly={readOnly}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KnockoutBracketTree;
